<template>
    <!-- Decorativo: o status do pedido já está em texto na página -->
    <svg class="otr" :class="{'is-moving': moving}" viewBox="0 0 66 42" aria-hidden="true" focusable="false">
        <!-- Fumaça saindo do escapamento, para trás -->
        <g class="otr-smoke">
            <circle class="otr-puff" cx="3" cy="31" r="2.4" />
            <circle class="otr-puff" cx="3" cy="31" r="2.4" />
            <circle class="otr-puff" cx="3" cy="31" r="2.4" />
        </g>

        <g class="otr-body">
            <!-- Baú na cor do canal, com o foguete do Muven na lateral: o canal vende, o Muven entrega -->
            <rect class="otr-box" x="5" y="4" width="37" height="25" rx="3.5" :style="{fill: color}" />
            <rect class="otr-box-shine" x="7" y="6" width="33" height="3" rx="1.5" />
            <image class="otr-logo" :href="muvenLogo" x="15" y="7.5" width="17" height="17" />

            <!-- Cabine -->
            <path class="otr-cab" d="M43 11 h11 l8 9 v8.5 a2.5 2.5 0 0 1 -2.5 2.5 H43 z" :style="{fill: cabColor}" />
            <path class="otr-window" d="M46 14 h7 l5.5 6.2 h-12.5 z" />
            <rect class="otr-light" x="59.5" y="24" width="2.5" height="2.2" rx="1" />

            <!-- Chassi e escapamento -->
            <rect x="4" y="28" width="58" height="3.4" rx="1.4" :style="{fill: cabColor}" />
            <rect x="1.5" y="30" width="4" height="1.8" rx=".9" :style="{fill: cabColor}" />
        </g>

        <g class="otr-wheel">
            <circle class="otr-tire" cx="16" cy="33.5" r="5.5" />
            <circle class="otr-hub" cx="16" cy="33.5" r="2.4" />
            <path class="otr-spoke" d="M16 29.5 V37.5 M12 33.5 H20" />
        </g>
        <g class="otr-wheel">
            <circle class="otr-tire" cx="52" cy="33.5" r="5.5" />
            <circle class="otr-hub" cx="52" cy="33.5" r="2.4" />
            <path class="otr-spoke" d="M52 29.5 V37.5 M48 33.5 H56" />
        </g>
    </svg>
</template>

<script>
import { getChannelBrand } from '@/channel/models/ChannelBrand'

/**
 * Caminhãozinho baú na cor do canal, com o foguete do Muven no baú. `moving` liga as
 * rodas, a fumaça e o sacolejo; quem usa decide onde ele fica e para onde vai.
 */
export default {
    name: 'OrderTruckCmp',
    props: {
        channel: {
            type: Object,
            required: true,
        },
        moving: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        brand() {
            return getChannelBrand(this.channel && this.channel.enumChannel)
        },
        color() {
            return this.brand.color
        },
        // A cabine é a mesma cor, mais fechada, para o caminhão ter volume
        cabColor() {
            return `color-mix(in srgb, ${this.brand.color} 72%, var(--shadow-ink))`
        },
        // Ícone oficial do Muven (o foguete), o mesmo do menu lateral e das caixas da trilha
        muvenLogo() {
            return require('@/assets/logo-muven.png')
        },
    },
}
</script>

<style lang="scss" scoped>
.otr {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
}

.otr-box,
.otr-cab {
    stroke: var(--border-strong);
    stroke-width: .6;
}

.otr-box-shine {
    fill: var(--white);
    opacity: .18;
}

// Contorno claro fino: separa o ícone roxo de baús escuros ou roxos
.otr-logo {
    filter: drop-shadow(0 0 .6px var(--white));
}

.otr-window {
    fill: var(--white);
    opacity: .85;
}

.otr-light {
    fill: var(--icon-highlight);
}

.otr-tire {
    fill: var(--surface-tooltip-deep);
    stroke: var(--border-strong);
    stroke-width: .5;
}

.otr-hub {
    fill: var(--text-faint);
}

.otr-spoke {
    stroke: var(--surface-tooltip-deep);
    stroke-width: 1.2;
    stroke-linecap: round;
}

.otr-wheel {
    transform-box: fill-box;
    transform-origin: center;
}

.otr-body {
    transform-box: fill-box;
    transform-origin: center bottom;
}

.otr-puff {
    fill: var(--text-faint);
    opacity: 0;
    transform-box: fill-box;
    transform-origin: center;
}

// Em movimento: rodas girando, carroceria sacolejando e fumaça saindo em ondas
.is-moving {
    .otr-wheel {
        animation: otr-spin .5s linear infinite;
    }

    .otr-body {
        animation: otr-bounce .42s ease-in-out infinite;
    }

    .otr-puff {
        animation: otr-puff 1.5s ease-out infinite;

        &:nth-child(2) { animation-delay: .5s; }
        &:nth-child(3) { animation-delay: 1s; }
    }
}

@keyframes otr-spin {
    to {
        transform: rotate(360deg);
    }
}

@keyframes otr-bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-.8px); }
}

@keyframes otr-puff {
    0% {
        opacity: .55;
        transform: translate(0, 0) scale(.6);
    }

    100% {
        opacity: 0;
        transform: translate(-9px, -9px) scale(2.2);
    }
}

@media (prefers-reduced-motion: reduce) {
    .is-moving {
        .otr-wheel,
        .otr-body,
        .otr-puff {
            animation: none;
        }
    }
}
</style>
